import { momentsApi, momentsBearerQuery } from './momentsApi';

import type { MomentsQueryError } from './momentsApi';

// Wire shapes mirror Moments pkg/schedulingassistant/http_handler.go (MEN-5598).
export type SchedulingAssistantStatus = 'pending' | 'active';

export interface SchedulingAssistant {
  addedByRole: 'member' | 'admin';
  confirmedAt: string | null;
  createdAt: string;
  displayName: string;
  email: string;
  id: string;
  status: SchedulingAssistantStatus;
}

export interface SchedulingAssistantList {
  assistants: SchedulingAssistant[];
  limit: number;
}

// A member manages their own assistants; Ops manage a member's from admin,
// naming the member by their Rails user id.
export type SchedulingAssistantOwner =
  { kind: 'self' } | { kind: 'admin'; memberId: string };

export interface AddSchedulingAssistantArg {
  displayName: string;
  email: string;
  owner: SchedulingAssistantOwner;
}

export interface SchedulingAssistantActionArg {
  id: string;
  owner: SchedulingAssistantOwner;
}

export type SchedulingAssistantErrorCode =
  | 'assistant_exists'
  | 'assistant_limit'
  | 'invalid_display_name'
  | 'invalid_email'
  | 'member_not_found'
  | 'not_found'
  | 'not_pending'
  | 'resend_limit';

interface SchedulingAssistantWire {
  added_by_role: 'member' | 'admin';
  confirmed_at: string | null;
  created_at: string;
  display_name: string;
  email: string;
  id: string;
  status: SchedulingAssistantStatus;
}

interface SchedulingAssistantListWire {
  assistants: SchedulingAssistantWire[];
  limit: number;
}

const mapSchedulingAssistant = (
  wire: SchedulingAssistantWire,
): SchedulingAssistant => ({
  addedByRole: wire.added_by_role,
  confirmedAt: wire.confirmed_at,
  createdAt: wire.created_at,
  displayName: wire.display_name,
  email: wire.email,
  id: wire.id,
  status: wire.status,
});

const mapSchedulingAssistantList = (
  payload: unknown,
): SchedulingAssistantList => {
  const wire = payload as SchedulingAssistantListWire;
  return {
    assistants: wire.assistants.map(mapSchedulingAssistant),
    limit: wire.limit,
  };
};

const ownerKey = (owner: SchedulingAssistantOwner): string =>
  owner.kind === 'self' ? 'self' : `admin:${owner.memberId}`;

const ownerTag = (owner: SchedulingAssistantOwner) => ({
  id: ownerKey(owner),
  type: 'SchedulingAssistant' as const,
});

const buildPath = (owner: SchedulingAssistantOwner, suffix = ''): string => {
  if (owner.kind === 'self') {
    return `/api/v1/me/scheduling-assistants${suffix}`;
  }
  const query = new URLSearchParams({
    member_external_id: owner.memberId,
    member_provider: 'legacy_app',
  });
  return `/api/v1/platform/admin/scheduling-assistants${suffix}?${query.toString()}`;
};

const assistantPath = (
  owner: SchedulingAssistantOwner,
  id: string,
  action = '',
): string => buildPath(owner, `/${encodeURIComponent(id)}${action}`);

const KNOWN_ERROR_CODES: readonly SchedulingAssistantErrorCode[] = [
  'assistant_exists',
  'assistant_limit',
  'invalid_display_name',
  'invalid_email',
  'member_not_found',
  'not_found',
  'not_pending',
  'resend_limit',
];

// Moments answers errors as {"error": "<code>"}.
export const schedulingAssistantErrorCode = (
  error: unknown,
): SchedulingAssistantErrorCode | null => {
  const data = (error as MomentsQueryError | undefined)?.data;
  if (data === null || typeof data !== 'object') return null;
  const code = (data as { error?: unknown }).error;
  return KNOWN_ERROR_CODES.find((known) => known === code) ?? null;
};

export const schedulingAssistantApi = momentsApi.injectEndpoints({
  endpoints: (builder) => ({
    addSchedulingAssistant: builder.mutation<
      SchedulingAssistant,
      AddSchedulingAssistantArg
    >({
      invalidatesTags: (_result, _error, { owner }) => [ownerTag(owner)],
      queryFn: async ({ displayName, email, owner }, queryApi) =>
        momentsBearerQuery(queryApi, {
          body: { display_name: displayName, email },
          fallbackData: undefined as unknown as SchedulingAssistant,
          mapResponse: (payload) =>
            mapSchedulingAssistant(payload as SchedulingAssistantWire),
          method: 'POST',
          path: buildPath(owner),
          returnErrorOnFailure: true,
        }),
    }),
    getSchedulingAssistants: builder.query<
      SchedulingAssistantList,
      SchedulingAssistantOwner
    >({
      providesTags: (_result, _error, owner) => [ownerTag(owner)],
      queryFn: async (owner, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: undefined as unknown as SchedulingAssistantList,
          mapResponse: mapSchedulingAssistantList,
          path: buildPath(owner),
          returnErrorOnFailure: true,
        }),
    }),
    removeSchedulingAssistant: builder.mutation<
      null,
      SchedulingAssistantActionArg
    >({
      invalidatesTags: (_result, _error, { owner }) => [ownerTag(owner)],
      queryFn: async ({ id, owner }, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: null,
          mapResponse: () => null,
          method: 'DELETE',
          path: assistantPath(owner, id),
          returnErrorOnFailure: true,
        }),
    }),
    resendSchedulingAssistantConfirmation: builder.mutation<
      SchedulingAssistant,
      SchedulingAssistantActionArg
    >({
      invalidatesTags: (_result, _error, { owner }) => [ownerTag(owner)],
      queryFn: async ({ id, owner }, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: undefined as unknown as SchedulingAssistant,
          mapResponse: (payload) =>
            mapSchedulingAssistant(payload as SchedulingAssistantWire),
          method: 'POST',
          path: assistantPath(owner, id, '/resend-confirmation'),
          returnErrorOnFailure: true,
        }),
    }),
  }),
});

export const {
  useAddSchedulingAssistantMutation,
  useGetSchedulingAssistantsQuery,
  useRemoveSchedulingAssistantMutation,
  useResendSchedulingAssistantConfirmationMutation,
} = schedulingAssistantApi;
